import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SectionTitle from "../ui/SectionTitle";
// Visible labels replace the old placeholder-only fields, which left screen
// readers and anyone mid-typing with no cue about what a box was for.
import { fieldClass, labelClass } from "../ui/formStyles";
import { CONTACT_SUBJECT_EVENT } from "../ui/contactIntent";
import { COMPANY } from "../../constants/legal";
import { SendContactMessageAction } from "../../store/action";

/** Field caps, mirroring the ones ContactMessageServices enforces server-side. */
const LIMITS = {
  name: 100,
  email: 254,
  subject: 150,
  message: 5000,
  minMessage: 10,
};

/**
 * Turns a failed submission into something worth showing a visitor.
 *
 * Deliberately does not surface the server's own sentence: the API answers in
 * English and this page is bilingual, so an English validation line in a
 * Romanian error box is worse than a specific translated one. The statuses are
 * the ones the endpoint actually returns.
 */
const describeFailure = (error, t) => {
  const status = error?.response?.status;

  if (status === 429) return t("Contact.form.tooManyMessages");
  if (status === 400) return t("Contact.form.invalidMessage");

  return t("Contact.form.errorMessage");
};

/**
 * Contact Component
 * Displays contact information and a contact form
 *
 * @param {string} sectionTitle - Main section title
 * @param {string} sectionDescription - Section description
 * @param {Object} contactInfo - Contact information object
 * @param {Function} onSubmit - Overrides the default delivery. Optional, and
 *   nothing passes it today — the default is the real API call. It must stay a
 *   genuine override rather than the only way to deliver: this form spent a
 *   long time falling back to a one-second timer and a success message when no
 *   handler was given, which threw away every message typed on the live site.
 */
const Contact = ({
  sectionTitle,
  sectionDescription,
  contactInfo = {},
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    // Honeypot. Stays empty for anyone who can see the form; the server drops
    // any submission that arrives with it filled.
    website: "",
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    error: "",
    success: false,
  });

  // A pricing card (the Custom "Contact us") can preload the subject so a
  // pricing/hosting enquiry arrives labelled. The anchor jump to #contact brings
  // the visitor here; this just fills the field they land on. Merges into
  // whatever they may have already typed rather than clobbering the form.
  useEffect(() => {
    const handleSubjectRequest = (event) => {
      const subject = event.detail;
      if (subject) {
        setFormData((prev) => ({ ...prev, subject }));
      }
    };

    window.addEventListener(CONTACT_SUBJECT_EVENT, handleSubjectRequest);
    return () =>
      window.removeEventListener(CONTACT_SUBJECT_EVENT, handleSubjectRequest);
  }, []);

  // The operator's real details, from the one place they are allowed to live.
  // A caller may still override them per-site; nothing does today.
  const defaultContactInfo = {
    address: COMPANY.registeredOffice,
    phone: COMPANY.phone,
    email: COMPANY.email,
  };

  const displayContactInfo =
    Object.keys(contactInfo).length > 0 ? contactInfo : defaultContactInfo;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset status
    setFormStatus({ loading: true, error: "", success: false });

    try {
      // The API keeps no copy of a contact message, so success here means the
      // call resolved — nothing else may set it.
      await (onSubmit ?? SendContactMessageAction)(formData);

      setFormStatus({ loading: false, error: "", success: true });

      // Reset form after success. The honeypot goes back to empty with it, or a
      // second message from the same visitor would look like a bot.
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        website: "",
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormStatus((prev) => ({ ...prev, success: false }));
      }, 5000);
    } catch (error) {
      // What they typed stays in the fields: the one thing worse than a failed
      // send is a failed send that also wipes the message.
      setFormStatus({
        loading: false,
        error: describeFailure(error, t),
        success: false,
      });
    }
  };

  return (
    <section id="contact" className="contact bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle
          title={sectionTitle || t("Contact.title")}
          description={sectionDescription || t("Contact.description")}
        />

        {/* Contact Info */}
        <div className="contact-info mt-14 grid gap-5 sm:grid-cols-3">
          <div className="contact-address flex flex-col items-center rounded-card border border-ink-200 bg-white p-7 text-center shadow-card">
            <span className="inline-flex size-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <i className="bi bi-geo-alt text-xl" aria-hidden="true" />
            </span>
            <h3 className="mt-4 text-xs font-bold uppercase tracking-wider text-ink-400">
              {t("Contact.address")}
            </h3>
            <address className="mt-2 text-fluid-sm not-italic leading-relaxed text-ink-700">
              {displayContactInfo.address}
            </address>
          </div>

          <div className="contact-phone flex flex-col items-center rounded-card border border-ink-200 bg-white p-7 text-center shadow-card">
            <span className="inline-flex size-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <i className="bi bi-phone text-xl" aria-hidden="true" />
            </span>
            <h3 className="mt-4 text-xs font-bold uppercase tracking-wider text-ink-400">
              {t("Contact.phone")}
            </h3>
            <p className="mt-2 text-fluid-sm text-ink-700">
              <a
                href={`tel:${displayContactInfo.phone.replace(/\s/g, "")}`}
                className="hover:text-brand-700"
              >
                {displayContactInfo.phone}
              </a>
            </p>
          </div>

          <div className="contact-email flex flex-col items-center rounded-card border border-ink-200 bg-white p-7 text-center shadow-card">
            <span className="inline-flex size-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <i className="bi bi-envelope text-xl" aria-hidden="true" />
            </span>
            <h3 className="mt-4 text-xs font-bold uppercase tracking-wider text-ink-400">
              {t("Contact.email")}
            </h3>
            <p className="mt-2 break-all text-fluid-sm text-ink-700">
              <a
                href={`mailto:${displayContactInfo.email}`}
                className="hover:text-brand-700"
              >
                {displayContactInfo.email}
              </a>
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="form mx-auto mt-6 max-w-3xl">
          <form
            onSubmit={handleSubmit}
            className="php-email-form relative rounded-card border border-ink-200 bg-white p-6 shadow-card sm:p-8"
          >
            {/*
              Honeypot. Positioned off-screen rather than `hidden`, because a
              fair share of form bots skip display:none inputs and fill
              everything else. Hidden from assistive tech and removed from the
              tab order, so nobody who can read the page ever lands in it —
              which is what makes a filled value proof of a script.
            */}
            <div
              className="absolute left-[-9999px] h-px w-px overflow-hidden"
              aria-hidden="true"
            >
              <label htmlFor="contact-website">
                {t("Contact.form.websiteLabel")}
              </label>
              <input
                type="text"
                name="website"
                id="contact-website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="form-group">
                <label htmlFor="name" className={labelClass}>
                  {t("Contact.form.namePlaceholder")}
                </label>
                <input
                  type="text"
                  name="name"
                  className={fieldClass}
                  id="name"
                  placeholder={t("Contact.form.namePlaceholder")}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={4}
                  maxLength={LIMITS.name}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email" className={labelClass}>
                  {t("Contact.form.emailPlaceholder")}
                </label>
                <input
                  type="email"
                  className={fieldClass}
                  name="email"
                  id="email"
                  placeholder={t("Contact.form.emailPlaceholder")}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={LIMITS.email}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label htmlFor="subject" className={labelClass}>
                {t("Contact.form.subjectPlaceholder")}
              </label>
              <input
                type="text"
                className={fieldClass}
                name="subject"
                id="subject"
                placeholder={t("Contact.form.subjectPlaceholder")}
                value={formData.subject}
                onChange={handleChange}
                required
                maxLength={LIMITS.subject}
              />
            </div>

            <div className="form-group mt-4">
              <label htmlFor="message" className={labelClass}>
                {t("Contact.form.messagePlaceholder")}
              </label>
              <textarea
                className={`${fieldClass} resize-y`}
                name="message"
                id="message"
                rows="5"
                placeholder={t("Contact.form.messagePlaceholder")}
                value={formData.message}
                onChange={handleChange}
                required
                minLength={LIMITS.minMessage}
                maxLength={LIMITS.message}
              />
            </div>

            <div aria-live="polite">
              {formStatus.loading && (
                <div className="loading mt-4 rounded-lg bg-ink-50 px-4 py-3 text-fluid-sm text-ink-600">
                  {t("Contact.form.loading")}
                </div>
              )}
              {formStatus.error && (
                <div
                  role="alert"
                  className="error-message mt-4 rounded-lg bg-red-50 px-4 py-3 text-fluid-sm text-red-700"
                >
                  <p>{formStatus.error}</p>
                  {/*
                    The message did not go anywhere and nothing kept a copy, so
                    the one thing this box owes the visitor is a channel that
                    does not depend on the form working.
                  */}
                  <p className="mt-1">
                    {t("Contact.form.errorFallback")}{" "}
                    <a
                      href={`mailto:${displayContactInfo.email}`}
                      className="font-semibold underline"
                    >
                      {displayContactInfo.email}
                    </a>
                  </p>
                </div>
              )}
              {formStatus.success && (
                <div className="sent-message mt-4 rounded-lg bg-brand-50 px-4 py-3 text-fluid-sm font-medium text-brand-800">
                  {t("Contact.form.successMessage")}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-center">
              <button
                type="submit"
                disabled={formStatus.loading}
                className="inline-flex items-center justify-center rounded-full bg-brand-600 px-8 py-3 text-sm font-semibold text-white shadow-brand transition hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("Contact.form.sendButton")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

Contact.propTypes = {
  sectionTitle: PropTypes.string,
  sectionDescription: PropTypes.string,
  contactInfo: PropTypes.shape({
    address: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
};

export default Contact;
